import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  checkIdentifierAvailability,
  register,
  upgradeGuestAccount,
} from "../services/authService";
import { useAuth } from "../services/AuthContext";
import Notification from "./Notification";
import EmailVerificationPanel from "./EmailVerificationPanel";
import { MdEmail, MdLock, MdPerson } from "react-icons/md";
import LanguageSelector from "./LanguageSelector";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { hasAcceptedTerms, markTermsAccepted } from "../constants/termsAndConditions";
import {
  getLanguageCopy,
  getStoredLanguage,
  setStoredLanguage,
} from "../services/language";
import { t as i18nT } from "../i18n";


const REGISTER_TEXTS = {
  title: {
    es: "REGISTER",
    en: "REGISTER",
    ru: "REGISTER",
    de: "REGISTRIEREN",
  },
  username: {
    es: "Nombre *",
    en: "Name *",
    ru: "Name *",
    de: "Name *",
  },
  usernamePlaceholder: {
    es: "Ingresa tu nombre de usuario",
    en: "Enter your username",
    ru: "Enter your username",
    de: "Benutzernamen eingeben",
  },
  usernameChecking: {
    es: "Verificando disponibilidad del nombre...",
    en: "Checking name availability",
    ru: "Checking username availability...",
    de: "Pruefe Benutzernamen...",
  },
  usernameAvailable: {
    es: "Nombre disponible.",
    en: "Name available",
    ru: "Username is available.",
    de: "Benutzername ist verfuegbar.",
  },
  usernameUnavailable: {
    es: "Nombre no disponible.",
    en: "Name not available",
    ru: "Username is not available.",
    de: "Benutzername ist nicht verfuegbar.",
  },
  usernameValidationError: {
    es: "No se pudo validar el nombre ahora mismo.",
    en: "Could not validate username right now.",
    ru: "Could not validate username right now.",
    de: "Benutzername konnte nicht geprueft werden.",
  },
  usernameRequired: {
    es: "El nombre es obligatorio.",
    en: "Name is required.",
    ru: "Name is required.",
    de: "Name ist erforderlich.",
  },
  usernameMustNotBeEmail: {
    es: "Escribe un nombre de usuario, no un correo electrónico.",
    en: "Enter a username, not an email address.",
    ru: "Введите имя пользователя, а не адрес электронной почты.",
    de: "Gib einen Benutzernamen ein, keine E-Mail-Adresse.",
  },
  usernameWait: {
    es: "Espera a que termine la verificacion del nombre.",
    en: "Please wait until username validation finishes.",
    ru: "Please wait until username validation finishes.",
    de: "Bitte warte auf das Ende der Namenspruefung.",
  },
  contact: {
    es: "Correo *",
    en: "Email *",
    ru: "Email *",
    de: "E-Mail *",
  },
  contactPlaceholder: {
    es: "Ingresa tu correo",
    en: "Enter your email",
    ru: "Enter your email",
    de: "E-Mail eingeben",
  },
  contactRequired: {
    es: "El correo es obligatorio.",
    en: "Email is required.",
    ru: "Email is required.",
    de: "E-Mail ist erforderlich.",
  },
  contactInvalid: {
    es: "Ingresa un correo valido.",
    en: "Enter a valid email.",
    ru: "Enter a valid email.",
    de: "Gib eine gueltige E-Mail ein.",
  },
  password: {
    es: "Contrasena *",
    en: "Password *",
    ru: "Password *",
    de: "Passwort *",
  },
  passwordPlaceholder: {
    es: "Ingresa tu contrasena",
    en: "Enter your password",
    ru: "Enter your password",
    de: "Passwort eingeben",
  },
  passwordRequired: {
    es: "La contrasena es obligatoria.",
    en: "Password is required.",
    ru: "Password is required.",
    de: "Passwort ist erforderlich.",
  },
  loading: {
    es: "Registrando...",
    en: "Registering...",
    ru: "Registering...",
    de: "Registrierung...",
  },
  checking: {
    es: "Verificando nombre...",
    en: "Verifying name",
    ru: "Checking username...",
    de: "Name wird geprueft...",
  },
  submit: {
    es: "REGISTRAR",
    en: "REGISTER",
    ru: "REGISTER",
    de: "REGISTRIEREN",
  },
  hasAccount: {
    es: "Ya tienes cuenta?",
    en: "Already have an account?",
    ru: "Already have an account?",
    de: "Hast du bereits ein Konto?",
  },
  login: {
    es: "Entrar",
    en: "Log in",
    ru: "Login",
    de: "Anmelden",
  },
  failedTitle: {
    es: "Registro fallido",
    en: "Registration Failed",
    ru: "Registration Failed",
    de: "Registrierung fehlgeschlagen",
  },
  failedDescription: {
    es: "No se pudo conectar con el servidor. Intenta de nuevo.",
    en: "Could not reach server. Please try again.",
    ru: "Could not reach server. Please try again.",
    de: "Server konnte nicht erreicht werden. Bitte versuche es erneut.",
  },
  verificationTitle: {
    es: "Revisa tu correo",
    en: "Check your email",
    ru: "Check your email",
    de: "Pruefe deine E-Mail",
  },
  verificationDescription: {
    es: "Te enviamos un codigo para activar tu cuenta.",
    en: "We sent you a code to activate your account.",
    ru: "We sent you a code to activate your account.",
    de: "Wir haben dir einen Code zur Aktivierung gesendet.",
  },
  verificationResendDescription: {
    es: "La cuenta fue creada. Usa reenviar codigo para intentar otra vez.",
    en: "The account was created. Use resend code to try again.",
    ru: "The account was created. Use resend code to try again.",
    de: "Das Konto wurde erstellt. Sende den Code erneut.",
  },
  termsRegisterInline: {
    es: "Al presionar REGISTRAR aceptas los Términos y Condiciones.",
    en: "By pressing REGISTER, you accept the Terms and Conditions.",
    ru: "Нажимая REGISTER, вы принимаете Условия использования.",
    de: "Mit REGISTRIEREN akzeptierst du die Nutzungsbedingungen.",
  },
};

const PREAUTH_TERMS_SUBJECT = "preauth";
const REGISTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_LOOKS_LIKE_EMAIL_PATTERN = /@/;

const isGuestSessionUser = (userValue) => {
  if (userValue?.isGuest === true) return true;
  return [userValue?.loginIdentifier, userValue?.email, userValue?.username].some((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    return (
      normalized.startsWith("invitadoo") ||
      normalized.endsWith("@guest.xima.local")
    );
  });
};

const RegisterForm = () => {
  const { user, logout } = useAuth();
  const isGuestUpgrade = isGuestSessionUser(user);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [usernameInput, setUsernameInput] = useState("");
  const [contactInput, setContactInput] = useState("");
  const [verificationContact, setVerificationContact] = useState("");
  const [language, setLanguage] = useState(getStoredLanguage);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsModalLoading, setTermsModalLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: "",
  });
  const pendingRegistrationRef = useRef(null);
  const navigate = useNavigate();
  const t = useCallback(
    (key) => getLanguageCopy(language, REGISTER_TEXTS[key], key),
    [language]
  );

  useEffect(() => {
    setStoredLanguage(language);
  }, [language]);

  useEffect(() => {
    const username = String(usernameInput || "").trim();

    if (!username) {
      setUsernameStatus({
        checking: false,
        available: null,
        message: "",
      });
      return;
    }

    if (USERNAME_LOOKS_LIKE_EMAIL_PATTERN.test(username)) {
      setUsernameStatus({
        checking: false,
        available: false,
        message: t("usernameMustNotBeEmail"),
      });
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setUsernameStatus({
          checking: true,
          available: null,
          message: t("usernameChecking"),
        });

        const response = await checkIdentifierAvailability(username);
        if (cancelled) return;

        const isAvailable = Boolean(response?.data?.available);
        setUsernameStatus({
          checking: false,
          available: isAvailable,
          message:
            response?.data?.message ||
            (isAvailable ? t("usernameAvailable") : t("usernameUnavailable")),
        });
      } catch (error) {
        if (cancelled) return;
        setUsernameStatus({
          checking: false,
          available: null,
          message: t("usernameValidationError"),
        });
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [usernameInput, t]);

  const executeRegistration = useCallback(
    async ({ username, contact, passwordValue }) => {
      setLoading(true);
      try {
        const availability = await checkIdentifierAvailability(username);
        if (!availability?.data?.available) {
          setErrors({
            username: availability?.data?.message || t("usernameUnavailable"),
          });
          return;
        }

        const registrationPayload = {
          username,
          contact,
          password: passwordValue,
        };
        const response = isGuestUpgrade
          ? await upgradeGuestAccount(registrationPayload)
          : await register(registrationPayload);

        if (response?.data?.requiresEmailVerification) {
          setVerificationContact(response?.data?.contact || contact);
          setNotification({
            type: response?.data?.verificationEmailSent ? "success" : "warning",
            message: t("verificationTitle"),
            description: response?.data?.verificationEmailSent
              ? t("verificationDescription")
              : t("verificationResendDescription"),
          });
          return;
        }

        navigate("/login");
        return;
      } catch (error) {
        const backendErrors = error?.response?.data?.errors;
        if (Array.isArray(backendErrors)) {
          const mappedErrors = backendErrors.reduce((accumulator, currentError) => {
            if (currentError?.path) {
              accumulator[currentError.path] = currentError.msg;
            }
            return accumulator;
          }, {});
          setErrors(mappedErrors);
        } else {
          const backendField = String(error?.response?.data?.field || "")
            .trim()
            .toLowerCase();
          const backendMessage = error?.response?.data?.message;
          if (backendField && backendMessage) {
            if (backendField === "username") {
              setErrors({ username: backendMessage });
            } else if (backendField === "contact" || backendField === "email" || backendField === "phone") {
              setErrors({ contact: backendMessage });
            } else {
              setNotification({
                type: "error",
                message: t("failedTitle"),
                description: backendMessage,
              });
            }
          } else if (backendMessage) {
            const normalizedMessage = String(backendMessage).toLowerCase();
            if (normalizedMessage.includes("username")) {
              setErrors({ username: backendMessage });
            } else if (
              normalizedMessage.includes("email") ||
              normalizedMessage.includes("phone") ||
              normalizedMessage.includes("contact")
            ) {
              setErrors({ contact: backendMessage });
            } else {
              setNotification({
                type: "error",
                message: t("failedTitle"),
                description: backendMessage,
              });
            }
          } else {
            setNotification({
              type: "error",
              message: t("failedTitle"),
              description: error?.message || t("failedDescription"),
            });
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [isGuestUpgrade, navigate, t]
  );

  const onSubmit = async (event) => {
    event.preventDefault();
    const { password } = event.target.elements;
    const username = String(usernameInput || "").trim();
    const contact = String(contactInput || "").trim();
    const passwordValue = String(password?.value || "");

    setErrors({});

    const nextErrors = {};
    if (!username) {
      nextErrors.username = t("usernameRequired");
    } else if (USERNAME_LOOKS_LIKE_EMAIL_PATTERN.test(username)) {
      nextErrors.username = t("usernameMustNotBeEmail");
    } else if (usernameStatus.checking) {
      nextErrors.username = t("usernameWait");
    } else if (usernameStatus.available === false) {
      nextErrors.username = t("usernameUnavailable");
    }

    if (!contact) {
      nextErrors.contact = t("contactRequired");
    } else if (!REGISTER_EMAIL_PATTERN.test(contact.toLowerCase())) {
      nextErrors.contact = t("contactInvalid");
    }

    if (!passwordValue) {
      nextErrors.password = t("passwordRequired");
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    pendingRegistrationRef.current = {
      username,
      contact,
      passwordValue,
    };

    if (hasAcceptedTerms(PREAUTH_TERMS_SUBJECT)) {
      await executeRegistration(pendingRegistrationRef.current);
      pendingRegistrationRef.current = null;
      return;
    }

    setTermsModalOpen(true);
  };

  const onTermsAccept = useCallback(async () => {
    if (termsModalLoading || loading) return;
    const pendingRegistration = pendingRegistrationRef.current;
    if (!pendingRegistration) return;
    markTermsAccepted(PREAUTH_TERMS_SUBJECT);
    setTermsModalLoading(true);
    setTermsModalOpen(false);
    try {
      await executeRegistration(pendingRegistration);
    } finally {
      pendingRegistrationRef.current = null;
      setTermsModalLoading(false);
    }
  }, [executeRegistration, loading, termsModalLoading]);

  const onTermsCancel = useCallback(() => {
    if (termsModalLoading) return;
    setTermsModalOpen(false);
    pendingRegistrationRef.current = null;
  }, [termsModalLoading]);

  return (
    <div
      className="xima-mobile-scroll-main flex min-h-screen flex-col items-center justify-center bg-center bg-cover px-4"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(5, 4, 3, 0.94), rgba(26, 16, 8, 0.82)), url(/background.jpg)",
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-[#6e3b1f] bg-[#120d09]/95 p-8 shadow-[0_24px_70px_rgba(0,0,0,0.55)] backdrop-blur">
        <LanguageSelector
          language={language}
          onChange={setLanguage}
          compact
          showDescription={false}
          className="mb-5"
        />
        <h2 className="mb-4 text-center text-4xl font-extrabold text-[#ffd66b]">
          <div className="flex justify-between mb-4">
            <img src="/ximalogo.png" width={80} className="drop-shadow-[0_0_16px_rgba(247,181,49,0.35)]" alt={i18nT("common.ximaLogo")} />
            <img src="/ximalogo.png" width={80} className="drop-shadow-[0_0_16px_rgba(247,181,49,0.35)]" alt={i18nT("common.ximaLogo")} />
          </div>
          {t("title")}
        </h2>
        {verificationContact ? (
          <EmailVerificationPanel
            contact={verificationContact}
            language={language}
            onVerified={async () => {
              if (isGuestUpgrade) {
                await logout();
              }
              navigate("/login");
            }}
            onBack={isGuestUpgrade ? undefined : () => setVerificationContact("")}
          />
        ) : (
        <form onSubmit={onSubmit} noValidate>
          <div className="mb-4">
            <label className="block text-lg text-[#f8f2e6]/85">{t("username")}</label>
            <div className="flex items-center border-b border-[#8b5724] py-2">
              <MdPerson className="mr-2 text-[#ffd66b]" />
              <input
                type="text"
                name="username"
                autoComplete="nickname"
                inputMode="text"
                value={usernameInput}
                onChange={(event) => {
                  const value = event.target.value;
                  setUsernameInput(value);
                  if (errors.username) {
                    setErrors((previous) => {
                      const next = { ...previous };
                      delete next.username;
                      return next;
                    });
                  }
                }}
                className={`w-full px-2 bg-transparent text-white focus:outline-none ${
                  errors.username ? "border-red-500" : "border-gray-700"
                }`}
                required
                placeholder={t("usernamePlaceholder")}
              />
            </div>
            {errors.username && <p className="text-sm text-red-500">{errors.username}</p>}
            {!errors.username && usernameStatus.message ? (
              <p
                className={`text-sm ${
                  usernameStatus.available === true
                    ? "text-green-400"
                    : usernameStatus.available === false
                    ? "text-yellow-300"
                    : "text-[#ffd66b]"
                }`}
              >
                {usernameStatus.message}
              </p>
            ) : null}
          </div>

          <div className="mb-4">
            <label className="block text-lg text-[#f8f2e6]/85">{t("contact")}</label>
            <div className="flex items-center border-b border-[#8b5724] py-2">
              <MdEmail className="mr-2 text-[#ffd66b]" />
              <input
                type="email"
                name="contact"
                autoComplete="email"
                inputMode="email"
                value={contactInput}
                onChange={(event) => {
                  const value = event.target.value;
                  setContactInput(value);
                  if (errors.contact) {
                    setErrors((previous) => {
                      const next = { ...previous };
                      delete next.contact;
                      return next;
                    });
                  }
                }}
                className={`w-full px-2 bg-transparent text-white focus:outline-none ${
                  errors.contact ? "border-red-500" : "border-gray-700"
                }`}
                required
                placeholder={t("contactPlaceholder")}
              />
            </div>
            {errors.contact && <p className="text-sm text-red-500">{errors.contact}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-lg text-[#f8f2e6]/85">{t("password")}</label>
            <div className="flex items-center border-b border-[#8b5724] py-2">
              <MdLock className="mr-2 text-[#ffd66b]" />
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                className={`w-full h-10 px-2 bg-transparent text-white focus:outline-none ${
                  errors.password ? "border-red-500" : "border-gray-700"
                }`}
                required
                placeholder={t("passwordPlaceholder")}
              />
            </div>
            {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
          </div>

          <button
            type="submit"
            className={`w-full rounded-md bg-gradient-to-b from-[#ffd66b] to-[#f7a928] py-2 font-bold text-[#150d05] shadow-[0_10px_24px_rgba(247,181,49,0.24)] transition duration-200 hover:brightness-110 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={loading || usernameStatus.checking}
          >
            {loading ? t("loading") : usernameStatus.checking ? t("checking") : t("submit")}
          </button>
          <p className="mt-2 text-center text-xs text-[#f8f2e6]/60">{t("termsRegisterInline")}</p>

          <div className="mt-4 text-center">
            <p className="text-[#f8f2e6]/70">
              {t("hasAccount")}{" "}
              <Link to="/login" className="text-[#ffd66b] hover:underline">
                {t("login")}
              </Link>
            </p>
          </div>
        </form>
        )}

        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            description={notification.description}
            onClose={() => setNotification(null)}
          />
        )}
        <TermsAcceptanceModal
          open={termsModalOpen}
          mode="register"
          loading={termsModalLoading || loading}
          language={language}
          onAccept={onTermsAccept}
          onCancel={onTermsCancel}
        />
      </div>
    </div>
  );
};

export default RegisterForm;
